import React from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { ApolloProvider } from '@apollo/client';
import { ModalProvider } from 'react-modal-hook';
import { CssBaseline, ThemeProvider } from '@mui/material';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';

import { activeTenant, tenantError } from '@app/tenant';
import { applyDocumentHead } from '@app/tenant/applyDocumentHead';
import { TenantNotFound } from '@app/tenant/components/TenantNotFound';

// D6/R6: `@app/tenant` resolves the tenant at module-evaluation time, so this import must run
// BEFORE `@app/language/configs/i18n` (whose D8 overlay reads `activeTenant`) and before
// `@app/app/configs/theme` (whose palette is per-tenant, D2/D3) — both import `@app/tenant`
// themselves, so Node's module cache makes this one line load-bearing for import order, not the
// two imports below it. See `@app/tenant`'s header comment (§6.0).
import '@app/language/configs/i18n';
import { theme } from '@app/app/configs/theme';
import { reportBuildInfo } from '@app/app/configs/buildInfo';
import { apolloClient } from '@app/query/configs/appoloClient';
import { AuthProvider } from '@app/auth/components/AuthProvider';
import { SnackbarsProvider } from '@app/snackbars/components/SnackbarsProvider';

import { App } from './pages/_app';
import reportWebVitals from './reportWebVitals';

reportBuildInfo();

const root = ReactDOM.createRoot(document.getElementById('root') as HTMLElement);

if (tenantError) {
  // D7 fail-closed: unknown hostname or a missing env var for the resolved tenant. Render the
  // neutral screen only — no theme/i18n overlay/Amplify/analytics for either tenant.
  root.render(
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <TenantNotFound />
    </ThemeProvider>,
  );
} else {
  applyDocumentHead(activeTenant!);

  root.render(
    <ThemeProvider theme={theme}>
      <ApolloProvider client={apolloClient}>
        <SnackbarsProvider>
          <AuthProvider>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <ModalProvider>
                <BrowserRouter>
                  <App />
                  <CssBaseline />
                </BrowserRouter>
              </ModalProvider>
            </LocalizationProvider>
          </AuthProvider>
        </SnackbarsProvider>
      </ApolloProvider>
    </ThemeProvider>,
  );
}

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
